import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null); // {is_active, status, plan, trial_ends_at, period_end}
  const [impersonation, setImpersonation] = useState(null); // {active, company_id, name}

  const loadSubscription = useCallback(async (u) => {
    if (!u) {
      setSubscription(null);
      return;
    }
    // Admins + super_admins get the full billing status. Employees get a
    // lightweight is-active check so the ProtectedRoute subscription gate
    // can decide whether to show the locked screen. Without this, employees
    // are trapped on an infinite loading spinner in ProtectedRoute's
    // subscription===null guard.
    try {
      if (u.role === "admin" || u.role === "super_admin") {
        const { data } = await api.get("/billing/status");
        setSubscription({
          is_active: !!data.is_active,
          status: data.company?.subscription_status || "none",
          plan: data.company?.plan,
          interval: data.company?.interval,
          trial_ends_at: data.company?.trial_ends_at,
          period_end: data.company?.period_end,
          comped: !!data.company?.comped,
        });
      } else {
        const { data } = await api.get("/billing/is-active");
        setSubscription({ is_active: !!data.is_active, status: "unknown" });
      }
    } catch {
      // On error, seed a definite {is_active:false} rather than null so the
      // routing gate can render the locked screen instead of spinning.
      setSubscription({ is_active: false, status: "unknown" });
    }
  }, []);

  const loadImpersonation = useCallback(async (u) => {
    if (!u || u.role !== "super_admin") {
      setImpersonation(null);
      return;
    }
    try {
      const { data } = await api.get("/super/active-tenant");
      setImpersonation(data.active ? { ...data } : null);
    } catch {
      setImpersonation(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await Promise.all([loadSubscription(data), loadImpersonation(data)]);
    } catch {
      setUser(false);
      setSubscription(null);
      setImpersonation(null);
    } finally {
      setLoading(false);
    }
  }, [loadSubscription, loadImpersonation]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for 402 subscription:required dispatches from the api interceptor
  // and surface a toast pointing the admin at the billing page. Then re-fetch
  // /billing/status so the banner persists.
  useEffect(() => {
    const onReq = (e) => {
      const msg = e.detail?.message || "An active subscription is required to use this feature.";
      toast.error(msg, {
        action: { label: "View plans", onClick: () => { window.location.href = "/admin/billing"; } },
      });
      // Defer the state-setting refresh out of this synchronous handler so
      // React doesn't flag set-state-in-effect.
      setTimeout(() => { loadSubscription(user); }, 0);
    };
    window.addEventListener("subscription:required", onReq);
    return () => window.removeEventListener("subscription:required", onReq);
  }, [user, loadSubscription]);

  const login = async (email, password, turnstileToken) => {
    try {
      const body = { email, password };
      if (turnstileToken) body.turnstile_token = turnstileToken;
      const { data } = await api.post("/auth/login", body);
      setUser(data.user);
      await Promise.all([loadSubscription(data.user), loadImpersonation(data.user)]);
      return { ok: true, user: data.user };
    } catch (e) {
      // Distinguish server-responded errors from network / CORS failures. If
      // there's no `e.response`, the request never made it to the API (offline,
      // DNS, blocked cookies, CORS, etc.) — surface the real reason instead of
      // the generic "Something went wrong" fallback that hides the root cause.
      if (!e.response) {
        return {
          ok: false,
          error: `Can't reach the server (${e.message || "network error"}). Check your internet connection and try again.`,
        };
      }
      const status = e.response.status;
      const detail = e.response.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
        return { ok: false, error: detail.message, code: "email_not_verified", email: detail.email };
      }
      // Server responded with an error but no useful detail — expose the HTTP
      // status so support can triage instead of showing a dead-end message.
      const serverMsg = formatApiError(detail);
      const isGeneric = !detail || serverMsg === "Something went wrong. Please try again.";
      return {
        ok: false,
        error: isGeneric
          ? `Sign-in failed (HTTP ${status}). ${status === 401 ? "Invalid email or password." : status >= 500 ? "The server hit an error — try again in a moment or contact support if it keeps happening." : "Please try again."}`
          : serverMsg,
      };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(false);
    setSubscription(null);
    setImpersonation(null);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, refresh,
      subscription, reloadSubscription: () => loadSubscription(user),
      impersonation, reloadImpersonation: () => loadImpersonation(user),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
